import { useEffect, useId, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { MODAL_INPUT_CLASS, ModalFooter, ModalHeader, ModalInput, ModalTextarea } from '@/components/admin/editorial-modal';
import { Button } from '@/components/ui/button';
import { ChipRadioGroup } from '@/components/ui/chip-radio-group';
import { Check, Coffee, Moon, Pill, Plus, Sun, Sunrise, Sunset } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { labelFor } from '@/i18n/labels';
import { MED_FORM_LABEL_KEY, MED_FREQUENCY_LABEL_KEY, TIME_OF_DAY_SLOTS } from '@/lib/medical-display';
import type { Medication } from '@/types/api';

const SLOT_ICON: Record<string, typeof Sun> = {
  morning: Sunrise,
  midday:  Sun,
  evening: Sunset,
  bedtime: Moon,
};

const FREQUENCIES: Medication['frequency'][] = [
  'once_daily',
  'twice_daily',
  'three_times_daily',
  'every_x_hours',
  'weekly',
  'as_needed',
  'other',
];

const FORMS: Medication['form'][] = ['pill', 'liquid', 'injection', 'patch', 'other'];

interface AddMedicationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the modal hydrates from this record and submits an update
   *  instead of a create — same pattern as AddAppointmentModal's `editing`. */
  editing?: Medication | null;
  onSubmit: (data: Partial<Medication>) => void;
  isSubmitting?: boolean;
}

const EMPTY: Partial<Medication> = {
  medication_name: '',
  generic_name: '',
  dose: '',
  form: 'pill',
  frequency: 'once_daily',
  time_of_day: [],
  with_food: false,
  prescribing_doctor: '',
  purpose: '',
  start_date: '',
  refill_reminder: false,
  refill_date: '',
  notes: '',
};

export function AddMedicationModal({ open, onOpenChange, editing, onSubmit, isSubmitting }: AddMedicationModalProps) {
  const { t } = useTranslation(['health', 'common']);
  const [form, setForm] = useState<Partial<Medication>>(EMPTY);
  const isEdit = Boolean(editing);
  const frequencyLabelId = useId();
  const timeOfDayLabelId = useId();

  // Hydrate from `editing` whenever the modal opens with an existing record
  // (date fields arrive as ISO timestamps; the date inputs want YYYY-MM-DD).
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        medication_name: editing.medication_name,
        generic_name: editing.generic_name ?? '',
        dose: editing.dose,
        form: editing.form,
        frequency: editing.frequency,
        time_of_day: editing.time_of_day ?? [],
        with_food: editing.with_food,
        prescribing_doctor: editing.prescribing_doctor ?? '',
        purpose: editing.purpose ?? '',
        start_date: editing.start_date ? editing.start_date.slice(0, 10) : '',
        refill_reminder: editing.refill_reminder,
        refill_date: editing.refill_date ? editing.refill_date.slice(0, 10) : '',
        notes: editing.notes ?? '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, editing]);

  const valid = (form.medication_name?.trim() ?? '') && (form.dose?.trim() ?? '') && form.frequency;
  const showSchedule = form.frequency !== 'as_needed' && form.frequency !== 'weekly';

  const toggleSlot = (slot: 'morning' | 'midday' | 'evening' | 'bedtime') => {
    setForm((f) => {
      const cur = f.time_of_day ?? [];
      return {
        ...f,
        time_of_day: cur.includes(slot) ? cur.filter((s) => s !== slot) : [...cur, slot],
      };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl flex flex-col p-0 gap-0 max-h-[90vh]">
        <ModalHeader
          icon={Pill}
          eyebrow={isEdit ? t('health:modal.medication.editEyebrow') : t('health:modal.medication.newEyebrow')}
          title={isEdit ? t('health:modal.medication.editTitle') : t('health:modal.medication.newTitle')}
        />

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5 min-h-0">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-3">
            <Field label={t('health:modal.medication.field.medication')}>
              <ModalInput
                type="text"
                value={form.medication_name ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, medication_name: e.target.value }))}
                placeholder={t('health:modal.medication.placeholder.medication')}
                autoFocus
              />
            </Field>
            <Field label={t('health:modal.medication.field.dose')}>
              <ModalInput
                type="text"
                value={form.dose ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, dose: e.target.value }))}
                placeholder={t('health:modal.medication.placeholder.dose')}
                className="font-mono"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t('health:modal.medication.field.genericName')}>
              <ModalInput
                type="text"
                value={form.generic_name ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, generic_name: e.target.value }))}
                placeholder={t('health:modal.medication.placeholder.genericName')}
              />
            </Field>
            <Field label={t('health:modal.medication.field.form')}>
              <select
                value={form.form ?? 'pill'}
                onChange={(e) => setForm((f) => ({ ...f, form: e.target.value as Medication['form'] }))}
                className={MODAL_INPUT_CLASS}
              >
                {FORMS.map((f) => (
                  <option key={f} value={f}>{labelFor(MED_FORM_LABEL_KEY, f, t)}</option>
                ))}
              </select>
            </Field>
          </div>

          <FieldGroup label={t('health:modal.medication.field.frequency')} labelId={frequencyLabelId}>
            <ChipRadioGroup
              labelledBy={frequencyLabelId}
              value={form.frequency ?? 'once_daily'}
              onValueChange={(freq) => setForm((f) => ({ ...f, frequency: freq }))}
              chipClassName="justify-center text-center"
              options={FREQUENCIES.map((freq) => ({ value: freq, label: labelFor(MED_FREQUENCY_LABEL_KEY, freq, t) }))}
            />
          </FieldGroup>

          {showSchedule && (
            <FieldGroup label={t('health:modal.medication.field.timeOfDay')} labelId={timeOfDayLabelId}>
              <div role="group" aria-labelledby={timeOfDayLabelId} className="flex gap-2">
                {TIME_OF_DAY_SLOTS.map((slot) => {
                  const Icon = SLOT_ICON[slot.key];
                  const on = (form.time_of_day ?? []).includes(slot.key);
                  return (
                    <button
                      key={slot.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleSlot(slot.key)}
                      className={cn(
                        'flex-1 inline-flex flex-col items-center gap-1 rounded-md border py-2',
                        on
                          ? 'border-cat-violet-border bg-cat-violet text-cat-violet-fg'
                          : 'border-border bg-card text-stevens-ink-3 hover:bg-muted',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-meta uppercase font-medium">
                        {t(slot.labelKey)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </FieldGroup>
          )}

          <Field label={t('health:modal.medication.field.purpose')}>
            <ModalInput
              type="text"
              value={form.purpose ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}
              placeholder={t('health:modal.medication.placeholder.purpose')}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t('health:modal.medication.field.prescribingDoctor')}>
              <ModalInput
                type="text"
                value={form.prescribing_doctor ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, prescribing_doctor: e.target.value }))}
                placeholder={t('health:modal.medication.placeholder.prescribingDoctor')}
              />
            </Field>
            <Field label={t('health:modal.medication.field.startDate')}>
              <ModalInput
                type="date"
                value={form.start_date ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              />
            </Field>
          </div>

          <div className="flex items-center gap-6 flex-wrap">
            <label className="inline-flex items-center gap-2 cursor-pointer text-body">
              <input
                type="checkbox"
                checked={form.with_food ?? false}
                onChange={(e) => setForm((f) => ({ ...f, with_food: e.target.checked }))}
                className="h-4 w-4 rounded border-input text-stevens-indigo"
              />
              <span className="inline-flex items-center gap-1"><Coffee className="h-3 w-3" /> {t('health:modal.medication.withFood')}</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer text-body">
              <input
                type="checkbox"
                checked={form.refill_reminder ?? false}
                onChange={(e) => setForm((f) => ({ ...f, refill_reminder: e.target.checked }))}
                className="h-4 w-4 rounded border-input text-stevens-indigo"
              />
              <span>{t('health:modal.medication.refillReminder')}</span>
            </label>
          </div>

          {form.refill_reminder && (
            <Field label={t('health:modal.medication.field.refillDate')}>
              <ModalInput
                type="date"
                value={form.refill_date ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, refill_date: e.target.value }))}
              />
            </Field>
          )}

          <Field label={t('health:modal.medication.field.notes')}>
            <ModalTextarea
              value={form.notes ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder={t('health:modal.medication.placeholder.notes')}
              rows={2}
              className="resize-y"
            />
          </Field>
        </div>

        <ModalFooter
          wrap
          lead={
            <span className="text-label uppercase text-stevens-ink-3">
              {t('health:modal.medication.footer')}
            </span>
          }
        >
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            size="sm"
            variant="brand"
            disabled={!valid || isSubmitting}
            onClick={() => onSubmit(form)}
          >
            {isEdit ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            {isSubmitting ? t('common:states.saving') : isEdit ? t('common:actions.saveChanges') : t('health:modal.medication.submit')}
          </Button>
        </ModalFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-meta font-medium uppercase text-stevens-ink-3 mb-1.5 block">
        {label}
      </span>
      {children}
    </label>
  );
}

/** A heading over a chip group: a span the group points at with
 *  aria-labelledby, never a wrapping <label> (#2434). */
function FieldGroup({ label, labelId, children }: { label: string; labelId: string; children: React.ReactNode }) {
  return (
    <div>
      <span id={labelId} className="text-meta font-medium uppercase text-stevens-ink-3 mb-1.5 block">
        {label}
      </span>
      {children}
    </div>
  );
}
